import axios from 'axios';
import { useRef } from 'react';
import { areArraysIdentical, keyToLabel, getMetafieldValue } from "./GlobalHelpers"

export function useCreditFunctions(
	channels,
	setMetafieldsValues,
	metafieldsSaveTimeouts,
	setMetafieldsSaveTimeouts,
	pageContext,
	userContext,
	getMetafieldsByFieldConfig,
	isFieldVisible,
) {

	const saveQueue = useRef(Promise.resolve());

	/**
	 * sucht die Konfiguration für das aktuelle Feld aus den Fieldmappings aller Channels
	 * und gibt diese als Objekt zurück
	 * @param {String} meta_key 
	 * @param {Boolean} onlyVisible
	 * @param {Boolean} merged
	 */
	const getConfigsForField = (meta_key, onlyVisible = false, merged = false) => {
		let fieldConfigs = [];
		channels.forEach(channel => {
			const fieldConfig = channel.fieldmapping.find(fieldmapping => fieldmapping.meta_key === meta_key);
			if (fieldConfig) {
				// if no label was configured create it from meta_key
				if (!fieldConfig.hasOwnProperty("label"))
					fieldConfig.label = keyToLabel(fieldConfig.meta_key);
				fieldConfig.channels = [channel.channel_shortname];
				fieldConfigs.push(fieldConfig);
			}
		});

		if (onlyVisible) {
			// filtere Felder anhand von conditions
			fieldConfigs = fieldConfigs.filter(fieldConfig => isFieldVisible(fieldConfig));
		}


		// console.log(fieldConfigs);
		if (merged)
			fieldConfigs = mergeChannelsWithSameConfigForField(fieldConfigs);

		return fieldConfigs
	}

	const isChannelActive = (channel_shortname) => {
		const channel = channels.find(channel => channel.channel_shortname === channel_shortname);
		return channel && channel.enabled;
	}


	const saveMetafield = (metafield_id, value_key, value, saveToken) => {
		const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
		const save = async () => {
			try {
				for (let attempt = 0; ; attempt++) {
					try {
						await axios({
							url: `${process.env.REACT_APP_API_SERVICE_URL}/metafields/${metafield_id}`,
							data: { id: metafield_id, [value_key]: value },
							method: 'put',
							headers: userContext.getRequestAuthHeader()
						});
						break;
					} catch (err) {
						if (!err.response || err.response.status !== 429 || attempt >= 3) throw err;
						const retryAfter = err.response.headers && err.response.headers['retry-after'];
						const seconds = Number(retryAfter);
						const delay = retryAfter && !Number.isFinite(seconds)
							? Date.parse(retryAfter) - Date.now() : seconds * 1000;
						await wait(Math.max(1000 * (attempt + 1), Number.isFinite(delay) ? delay : 0));
					}
				}
				setMetafieldsSaveTimeouts(timeouts => {
					// A completed save must not clear a newer pending edit of the same field.
					if (timeouts[metafield_id] !== saveToken) return timeouts;
					const next = { ...timeouts };
					delete next[metafield_id];
					return next;
				});
			} catch (err) {
				console.log(err);
				pageContext.setErrorMessage("Das Feld konnte nicht gespeichert werden!");
			} finally {
				// Limit both concurrency and the rate of short requests.
				await wait(200);
			}
		};
		saveQueue.current = saveQueue.current.then(save, save);
		return saveQueue.current;
	}

	const saveMetafieldDelayed = (metafield_id, value_key, value) => {
		if (metafieldsSaveTimeouts[metafield_id])
			clearTimeout(metafieldsSaveTimeouts[metafield_id]);
		setMetafieldsSaveTimeouts(timeouts => {
			const saveToken = setTimeout(() => saveMetafield(metafield_id, value_key, value, saveToken), 2000);
			timeouts[metafield_id] = saveToken;
			return timeouts;
		});
	}

	const isWaitingForSaveOfPortalField = (personIndex, metafields) => {
		const metaKeys = [
			"__person" + personIndex + "__anrede",
			"__person" + personIndex + "__vorname",
			"__person" + personIndex + "__nachname",
			"__person" + personIndex + "__postleitzahl",
			"__person" + personIndex + "__ort",
			"__person" + personIndex + "__strasse",
			"__person" + personIndex + "__hausnummer",
			"__person" + personIndex + "__kontakt_mobil",
			"__person" + personIndex + "__kontakt_email"
		];

		const currentlySavingIds = Object.keys(metafieldsSaveTimeouts);
		// convert string keys to int
		currentlySavingIds.forEach((id, i) => {
			currentlySavingIds[i] = parseInt(id);
		});
		const currentlySavingPortalFields = metafields.filter(metafield =>
			currentlySavingIds.indexOf(metafield.id) > -1
			&& metaKeys.indexOf(metafield.meta_key) > -1
		);

		return currentlySavingPortalFields.length > 0;
	}


	/**
	 * Vereinfacht das Aktualisieren eines Metafields weil die Funktion den Typ prüft und die entsprechende Property setzt
	 * meta_value, meta_value_numeric oder meta_value_boolean
	 * Die Funktion speichert allerdings nicht sondern gibt nur ein aktualisiertes Array aller Metafields zurück
	 * @param {Array} allMetafields 
	 * @param {Object} metafield 
	 * @param {String|Boolean|Numeric} value 
	 * @returns 
	 */
	const setMetafieldValue = (allMetafields, metafield, value) => {
		let value_key = "meta_value";

		if (!metafield.multiple) {
			if (["INT_ENUM", "INT"].includes(metafield.field_type)) {
				value_key = "meta_value_numeric";
				value = parseInt(value);
				if (!value)
					value = 0;
			} else if (["FLOAT"].includes(metafield.field_type)) {
				value_key = "meta_value_numeric";
				value = parseFloat(value);
				if (!value)
					value = 0.0;
			} else if (["BOOLEAN"].includes(metafield.field_type)) {
				value_key = "meta_value_boolean";
				if (!value)
					value = false;
			} else {
				if (!value)
					value = "";
			}
		}


		const fieldIndex = allMetafields.findIndex(f => f.id === metafield.id);
		if (fieldIndex > -1) {
			const previous = allMetafields[fieldIndex][value_key];
			const emptyValue = value_key === "meta_value_numeric" ? 0
				: value_key === "meta_value_boolean" ? false : "";
			const normalizedPrevious = previous == null ? emptyValue : previous;
			const normalizedValue = value == null ? emptyValue : value;
			if (JSON.stringify(normalizedPrevious) === JSON.stringify(normalizedValue))
				return allMetafields;
			allMetafields[fieldIndex][value_key] = value;
		}



		saveMetafieldDelayed(metafield.id, value_key, value);
		return allMetafields
	}


	/**
	 * Ein Feld existiert üblicherweise mehrfach bei mehreren Banken
	 * Wenn man allerdings nur den meta_key hat bekommt man damit den Wert des ersten sichtbaren Feldes mit diesem key
	 * @param {string} meta_key 
	 * @param {Object} fieldConfig 
	 */
	const getFirstValueByMetaKey = (meta_key) => {
		const fieldConfigs = getConfigsForField(meta_key, true, false);
		if (fieldConfigs.length > 0) {
			const currentMetafields = getMetafieldsByFieldConfig(fieldConfigs[0]);
			if (currentMetafields.length > 0)
				return getMetafieldValue(currentMetafields[0]);
		}
		return null;
	}

	/**
	 * Hier könnte es mehrere Felder mirt dem gleichen Key und unterschiedlichen Channels geben
	 * Die Funktion setzt die erste gemergte Config davon und setMetafieldsValues kümmert sich um das setzen der related
	 * @param {string} meta_key 
	 * @param {mixed} value 
	 */
	const setValuesByMetaKey = (meta_key, value) => {
		const fieldConfigs = getConfigsForField(meta_key, true, true);
		if (fieldConfigs.length > 0) {
			const currentMetafields = getMetafieldsByFieldConfig(fieldConfigs[0]);
			if (currentMetafields.length > 0)
				setMetafieldsValues(currentMetafields, value, true);
		}
	}



	/**
 * Möglicherweise kann das Feld für mehrere Channels in einem ausgegeben werden zB bei STRING Feldern oder bei STRING_ENUM mit identischen Werten
 * @param {} fieldConfig 
 */
	const mergeChannelsWithSameConfigForField = (fieldConfigs) => {
		const mergedFieldConfigs = [];
		const usedFieldConfigIndizes = [];
		fieldConfigs.forEach((fieldConfig, i) => {
			if (usedFieldConfigIndizes.indexOf(i) === -1) {
				if (["STRING", "BOOLEAN", "INT", "DATE", "FILE"].includes(fieldConfig.field_type)) {
					// STRING und ... Felder brauchen nur den gleichen field_type um identisch zu sein
					fieldConfigs.forEach((otherFieldConfig, otherFieldIndex) => {
						if (otherFieldIndex !== i && otherFieldConfig.field_type === fieldConfig.field_type) {
							fieldConfig.channels.push(otherFieldConfig.channels[0])
							usedFieldConfigIndizes.push(otherFieldIndex);
						}
					})
				} else if (["STRING_ENUM", "INT_ENUM"].includes(fieldConfig.field_type)) {
					// bei _ENUM Feldern müssen auch die Options gleich sein
					fieldConfigs.forEach((otherFieldConfig, otherFieldIndex) => {
						// console.log("compare Arrays", fieldConfig.options, otherFieldConfig.options, areArraysIdentical(fieldConfig.options, otherFieldConfig.options))
						if (otherFieldIndex !== i && otherFieldConfig.field_type === fieldConfig.field_type && areArraysIdentical(fieldConfig.options, otherFieldConfig.options)) {
							fieldConfig.channels.push(otherFieldConfig.channels[0])
							usedFieldConfigIndizes.push(otherFieldIndex);
						}
					})
				}

				usedFieldConfigIndizes.push(i);
				mergedFieldConfigs.push(fieldConfig);
			}
		})

		return mergedFieldConfigs;
	}


	const resolvePlaceholders = (text) => {
		if (!text)
			return "";
		return text.replace(/(\[.+?\])/g, (match, contents, offset, input_string) => {
			const replacement = getFirstValueByMetaKey(match.substring(1, match.length - 1));
			if (replacement)
				return replacement;
			return "";
		});
	}

	const filename = (text) => {
		return resolvePlaceholders(text)
			.toLowerCase()
			.replaceAll("ä", "ae")
			.replaceAll("ö", "oe")
			.replaceAll("ü", "ue")
			.replaceAll("ß", "ss")
			.replace(/[^a-z0-9]/gi, '_')
	}

	const getFieldGroupProgress = (groupName, index = 0) => {
		let meta_keys = [];
		let percentComplete = 0;
		if (groupName === "liegenschaft" && index > 0) {
			meta_keys = [
				"objektdaten_liegenschaft" + index + "_objekt",
				"objektdaten_liegenschaft" + index + "_objekt_plz",
				"objektdaten_liegenschaft" + index + "_objekt_ort",
				"objektdaten_liegenschaft" + index + "_objekt_strasse",
				"objektdaten_liegenschaft" + index + "_objekt_hausnummer",
				"objektdaten_liegenschaft" + index + "_bezirksgericht",
				"objektdaten_liegenschaft" + index + "_grundbuch",
				"objektdaten_liegenschaft" + index + "_ez",
				// Zusatzinfo wird ignoriert
			];
		} else if (groupName === "liegenschaft_tipp" && index > 0) {
			meta_keys = [
				"objektdaten_liegenschaft" + index + "_objekt",
			];
		} else if (groupName === "person" && index > 0) {
			meta_keys = [
				"__person" + index + "__anrede",
				"__person" + index + "__vorname",
				"__person" + index + "__nachname",
				"__person" + index + "__geburtsdatum",
				"__person" + index + "__familienstand",
				"__person" + index + "__nationalitaet",
				"__person" + index + "__postleitzahl",
				"__person" + index + "__ort",
				"__person" + index + "__strasse",
				"__person" + index + "__hausnummer",
				"__person" + index + "__wohnhaft_seit",
				"__person" + index + "__zukuenftiger_wohnstatus",
				"__person" + index + "__anzahl_personen_im_haushalt",
				"__person" + index + "__beruflicher_status",
				"__person" + index + "__monatliches_nettoeinkommen",
				// "__person" + index + "__branche",
				"__person" + index + "__beschaeftigt_seit",
				"__person" + index + "__kontakt_email",
				"__person" + index + "__kontakt_mobil",
			];
		} else if (groupName === "person_tipp" && index > 0) {
			meta_keys = [
				"__person" + index + "__anrede",
				"__person" + index + "__vorname",
				"__person" + index + "__nachname",
				"__person" + index + "__kontakt_email",
				"__person" + index + "__kontakt_mobil",
				"__person" + index + "__postleitzahl",
				"__person" + index + "__ort",
				"__person" + index + "__strasse",
				"__person" + index + "__hausnummer",
			];
		} else if (groupName === "vorlast" && index > 0) {
			meta_keys = [
				"__vorlast" + index + "__kreditnehmer",
				"__vorlast" + index + "__institut",
				"__vorlast" + index + "__vorbelastung_fuer",
				"__vorlast" + index + "__urspr_kreditbetrag",
				"__vorlast" + index + "__monatliche_rate",
				"__vorlast" + index + "__aktuelle_aushaftung",
			];
		}
		if (meta_keys.length > 0) {
			let numComplete = 0;
			meta_keys.forEach(meta_key => {
				if (getFirstValueByMetaKey(meta_key))
					numComplete++;
			})
			percentComplete = Math.round(numComplete / meta_keys.length * 100);
		}
		return percentComplete;
	}

	const matchOptionByLookupList = (currentFieldConfig, value, options) => {
		let lookuplist = [];
		if (currentFieldConfig.meta_key.endsWith("__bankverbindung")) {
			lookuplist = {
				"ALLGEMEINE_SPARKASSE_OBEROESTERREICH": "Sonstiges",
				"BANK_AUSTRIA": "Bank Austria",
				"BANK_FUER_AERZTE": "Sonstiges",
				"BANK_TIROL": "Sonstiges",
				"KRENTSCHKER": "Sonstiges",
				"SCHELLHAMMER": "Sonstiges",
				"BAWAG_PSK": "BAWAG",
				"BKS": "Sonstiges",
				"DEUTSCHE_BANK": "Sonstiges",
				"DIREKTANLAGE_AT": "Sonstiges",
				"DORNBIRNER": "Sonstiges",
				"EASYBANK": "Easy Bank",
				"ERSTE_BANK": "Erste Bank",
				"GAERTNER_BANK": "Sonstiges",
				"GENERALI": "Sonstiges",
				"HYPO_ALPE_ADRIA": "Hypo",
				"HYPO_NOE": "Hypo",
				"HYPO_TIROL": "Hypo",
				"HYPO_BURGENLAND": "Hypo",
				"IMMO_BANK": "Sonstiges",
				"KAERTNER_SPARKASSE": "Erste Bank",
				"KATHREIN": "Sonstiges",
				"KREMSER": "Sonstiges",
				"LANDES_HYPO_STMK": "Hypo",
				"OBERBANK": "Oberbank",
				"OBEROESTERR_LANDESBANK": "Sonstiges",
				"APOTHERKERBANK": "Sonstiges",
				"VOLKSBANKEN_AG": "Sonstiges",
				"RAIFFEISEN": "Raiffeisen",
				"SALZBURGER_SPARKASSE": "Erste Bank",
				"SPARDA": "Sonstiges",
				"SPARKASSEN": "Erste Bank",
				"STEIERMAERKISCHE_BANK": "Erste Bank",
				"TIROLER_SPARKASSE": "Erste Bank",
				"VOLKSBANK": "Sonstiges",
				"VORARLBERGER": "Sonstiges",
				"WALDVIERTLER": "Sonstiges",
				"WR_NEUSTAEDTER": "Sonstiges",
				"WSK": "Sonstiges",
				"SONSTIGE": "Sonstiges",
			}
		} else if (currentFieldConfig.meta_key.endsWith("_wohnstatus")) {
			lookuplist = {
				"EIGENTUMSWOHNUNG": "Eigentum",
				"EIGENHEIM": "Eigentum",
				"GENOSSENSCHAFTSWOHNUNG": "Genossenschaft",
				"MIETE": "Hauptmiete",
				"UNTERMIETER_MITBEWOHNER": "Mietwohnung/Untermiete/Sonstiges"
			};
		}
		// find value by key
		if (lookuplist[value])
			return lookuplist[value];
		// find keys by value
		let matchingKeysByValue = Object.keys(lookuplist).filter(key => lookuplist[key] === value);
		if (matchingKeysByValue.length === 1)
			return matchingKeysByValue[0];

		return null;
	}

	const resetExternalCreditIds = (credit_id) => {
		channels.forEach(channel => {
			if (channel.channel_shortname !== "GLOBAL" && channel.enabled) {
				const external_credit_id = getFirstValueByMetaKey(channel.channel_shortname + "_external_credit_id");
				if (external_credit_id) {
					setValuesByMetaKey(channel.channel_shortname + "_external_credit_id", null);
				}
			}
		});
	}

	return {
		getConfigsForField,
		saveMetafield,
		setMetafieldValue,
		getMetafieldValue,
		getFirstValueByMetaKey,
		setValuesByMetaKey,
		mergeChannelsWithSameConfigForField,
		getFieldGroupProgress,
		resolvePlaceholders,
		filename,
		matchOptionByLookupList,
		resetExternalCreditIds,
		isWaitingForSaveOfPortalField,
		isChannelActive,
	};
}